import { FormEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Eyebrow from '../components/Eyebrow';
import Wordmark from '../components/Wordmark';
import { ApiError, authApi } from '../lib/api';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [needsSetup, setNeedsSetup] = useState(() => new URLSearchParams(location.search).get('setup') === '1');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [setupSecret, setSetupSecret] = useState('');
  const [busy, setBusy] = useState(false);
  // AdminGuard sends an expired session here with its reason in navigation
  // state, because the admin Toaster unmounts before it could ever show one.
  const [error, setError] = useState<string | null>(
    () => (location.state as { message?: string } | null)?.message ?? null,
  );

  useEffect(() => {
    authApi
      .status()
      .then((status) => {
        if (status.needs_setup !== undefined) setNeedsSetup(status.needs_setup);
        setNeedsTotp(status.needs_totp);
      })
      .catch(() => setError('We could not check the admin account. Mind refreshing?'));
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (needsSetup && password !== confirm) {
      setError('Those passwords do not match. Mind trying again?');
      return;
    }
    setBusy(true);
    try {
      if (needsSetup) await authApi.setup({ password, setup_secret: setupSecret });
      else await authApi.login({ password, code: needsTotp ? code : undefined });
      const state = location.state as { from?: { pathname?: string } } | null;
      navigate(state?.from?.pathname ?? '/admin', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.status === 401 && /two-factor|code/i.test(caught.message)) setNeedsTotp(true);
        setError(caught.message);
      } else {
        setError('Something went wrong. Mind trying again?');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="shell auth-shell">
      <header className="shell-header">
        <Wordmark />
      </header>
      <section className="auth-stage">
        <form className="card auth-card rise" onSubmit={submit}>
          <Eyebrow accent>{needsSetup ? 'First run' : 'Admin'}</Eyebrow>
          <h1 className="title-xl rise mt-4" style={{ animationDelay: '60ms' }}>{needsSetup ? 'Set up your admin' : 'Sign in'}</h1>
          <p className="supporting-copy rise" style={{ animationDelay: '120ms' }}>
            {needsSetup
              ? 'Enter the deployment setup secret, then choose your admin password.'
              : 'Enter your admin password to continue.'}
          </p>
          <label className="field rise-subtle" style={{ animationDelay: '200ms' }}>
            <span className="mono-label">Password</span>
            <input
              className="input"
              type="password"
              autoComplete={needsSetup ? 'new-password' : 'current-password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              minLength={8}
              required
              autoFocus
              disabled={busy}
            />
          </label>
          {needsSetup ? (
            <>
              <label className="field rise-subtle" style={{ animationDelay: '240ms' }}>
                <span className="mono-label">Deployment setup secret</span>
                <input className="input" type="password" autoComplete="off" value={setupSecret} onChange={(event) => setSetupSecret(event.target.value)} required disabled={busy} />
              </label>
              <label className="field rise-subtle" style={{ animationDelay: '280ms' }}>
                <span className="mono-label">Confirm password</span>
                <input
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                  minLength={8}
                  required
                  disabled={busy}
                />
              </label>
            </>
          ) : null}
          {!needsSetup && needsTotp ? (
            <label className="field rise-subtle" style={{ animationDelay: '260ms' }}>
              <span className="mono-label">Two-factor code</span>
              <input
                className="input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\s/g, ''))}
                required
                disabled={busy}
              />
            </label>
          ) : null}
          {error ? <p className="error-copy" role="alert">{error}</p> : null}
          <button className="btn btn-solid app-button-glow rise-subtle" style={{ animationDelay: '320ms' }} type="submit" disabled={busy}>
            {busy ? 'One moment' : needsSetup ? 'Create admin' : 'Sign in'}
          </button>
        </form>
      </section>
    </main>
  );
}
